import {IAccountDetails, IAssessment, IAssessmentDto} from "./main.interfaces";
import {BackendRestAPIRequestTypes, createRequestOptions} from "./backend-api.util";
import {convertAssessmentToDto} from "@/store/assessment.util";
import {assembleAssessmentOutputs} from "@/store/assessment.assembly";
import {IOrganization} from "@/interfaces/organization.interface";
import {IProcessForm} from "@/interfaces/process-form.interface";
import {IRelease, IReleaseResponse} from "@/interfaces/orchestrator.interface";

interface IAssessmentApi {
  getAllAssessments(): Promise<IAssessment[]>;

  createOrUpdateAssessment(assessment: IAssessment): Promise<IAssessment>;

  deleteAssessment(id: string): Promise<boolean>;
}

class BackendApi implements IAssessmentApi {
  readonly REQUEST_URI = "/api/";
  readonly ASSESSMENTS = "assessments/";
  readonly ASSESSMENTS_NUMBER = "/number-of-assessments";
  readonly SEARCH = "search/";
  readonly ADMIN = "admin/";
  readonly LOGIN = "login";
  readonly LOGOUT = "logout";
  readonly FORGOT_PASSWORD = "forgot-password";
  readonly RESET_PASSWORD = "reset-password";
  readonly DEPARTMENT = "departments";
  readonly USERS = "users";
  readonly UPDATE_SELF = "updateSelf";
  readonly EDIT = "/edit/";
  readonly RESPONSE_STATUS_OK = 200;
  readonly PROCESS_FORMS = "processforms";
  readonly ORCHESTRATOR = "orchestrator";
  readonly GENERATE_PROCESS_TOKEN = this.PROCESS_FORMS + "/tokens/generate";
  readonly REDEEM_PROCESS_TOKEN = this.PROCESS_FORMS + "/tokens/redeem";

  async getAllAssessments(): Promise<IAssessment[]> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);

    const restResponse = await fetch(
        this.REQUEST_URI + this.ASSESSMENTS,
        requestOptions as RequestInit
    );
    const responseData: IAssessment[] = await restResponse.json();
    const isValidResponse = responseData && Array.isArray(responseData);

    if (isValidResponse) {
      const fetchedAssessments: IAssessment[] = await responseData.map(
          assessmentResponseObject => {
            const assessmentEntry: IAssessment = assessmentResponseObject;
            assessmentEntry.outputs = assembleAssessmentOutputs(
                assessmentEntry.inputs
            );
            return assessmentEntry;
          }
      );
      return fetchedAssessments;
    } else {
      return [];
    }
  }

  async createOrUpdateAssessment(
      assessment: IAssessment
  ): Promise<IAssessment> {
    let createOrUpdateResponse;
    if (assessment.id) {
      createOrUpdateResponse = await this.updateAssessment(assessment);
    } else {
      createOrUpdateResponse = await this.createAssessment(assessment);
    }
    if (createOrUpdateResponse.status === this.RESPONSE_STATUS_OK) {
      this.getAllAssessments();
      const createdAssessment: IAssessment = await createOrUpdateResponse.json();
      createdAssessment.outputs = assembleAssessmentOutputs(
          createdAssessment.inputs
      );
      return createdAssessment;
    } else {
      return {} as IAssessment;
    }
  }

  async updateAssessment(assessment: IAssessment): Promise<Response> {
    const assessmentDto: IAssessmentDto = convertAssessmentToDto(assessment);
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.PUT,
        assessmentDto
    );
    const updateAssessmentResponse = await fetch(
        this.REQUEST_URI + this.ASSESSMENTS + assessment.id,
        requestOptions as RequestInit
    );
    return updateAssessmentResponse;
  }

  async createAssessment(assessment: IAssessment): Promise<Response> {
    const assessmentDto: IAssessmentDto = convertAssessmentToDto(assessment);
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.POST,
        assessmentDto
    );
    const createAssessmentResponse = await fetch(
        this.REQUEST_URI + this.ASSESSMENTS,
        requestOptions as RequestInit
    );
    return createAssessmentResponse;
  }

  async deleteAssessment(id: string): Promise<boolean> {
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.DELETE
    );
    const getAllAssessmentsResponse = await fetch(
        this.REQUEST_URI + this.ASSESSMENTS + id,
        requestOptions as RequestInit
    );
    const requestStatus: boolean =
        getAllAssessmentsResponse.status === this.RESPONSE_STATUS_OK;
    this.getAllAssessments();
    return requestStatus;
  }

  async loginRequest(username?: string, password?: string) {
    let requestOptions: RequestInit = {};
    if (username && password) {
      const encodedString = this.b64EncodeUnicode(username + ":" + password);
      const authenticationString = "Basic " + encodedString;
      requestOptions = createRequestOptions(
          BackendRestAPIRequestTypes.POST,
          undefined,
          authenticationString
      );
    } else {
      requestOptions = createRequestOptions(
          BackendRestAPIRequestTypes.POST,
          undefined
      );
    }
    const loginResponse = await fetch(
        this.REQUEST_URI + this.LOGIN,
        requestOptions
    );
    if (loginResponse.status !== this.RESPONSE_STATUS_OK) {
      throw new Error("Login failed");
    }
    return await loginResponse.json();
  }

  async loginRequestWithoutAuthentication() {
    return await this.loginRequest();
  }

  /*
   * Function to convert from UTF8 to Base64 solving the Unicode Problem
   * Requires: window.btoa and window.encodeURIComponent functions
   * More info: http://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
   * Samples:
   *      b64EncodeUnicode('✓ à la mode'); // "4pyTIMOgIGxhIG1vZGU="
   *      b64EncodeUnicode('\n'); // "Cg=="
   */
  b64EncodeUnicode(str: string): string {
    return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
          return String.fromCharCode(("0x" + p1) as any);
        })
    );
  }

  async logoutRequest() {
    const requestOptions: RequestInit = createRequestOptions(
        BackendRestAPIRequestTypes.GET,
        undefined,
        undefined
    );
    const logoutResponse = await fetch(
        this.REQUEST_URI + this.LOGOUT,
        requestOptions
    );
    return logoutResponse;
  }

  async forgotPasswordRequest(username: string): Promise<boolean> {

    // prepare the data
    const userInfo: FormData = new FormData();
    userInfo.append("link", this.getResetPasswordLink());
    userInfo.append("user", username);

    const requestOptions = {
      method: BackendRestAPIRequestTypes.POST,
      body: userInfo
    };
    let response: Response = await fetch(
        this.REQUEST_URI + this.FORGOT_PASSWORD,
        requestOptions
    )
        .then(response => response)
        .catch(); // logging?
    if (response.status == this.RESPONSE_STATUS_OK) {
      return response.ok;
    } else {
      return false;
    }
  }

  async resetPasswordRequest(token: string, password: string): Promise<boolean> {

    // prepare the data
    const passwordInfo: FormData = new FormData();
    passwordInfo.append("token", token);
    passwordInfo.append("password", password);

    const requestOptions = {
      method: BackendRestAPIRequestTypes.POST,
      body: passwordInfo
    };
    let response: Response = await fetch(
        this.REQUEST_URI + this.RESET_PASSWORD,
        requestOptions
    )
        .then(response => response)
        .catch(); // logging?
    if (response.status == this.RESPONSE_STATUS_OK) {
      return response.ok;
    } else {
      return false;
    }
  }

  getResetPasswordLink(): string {
    return document.location.origin + "/" + this.RESET_PASSWORD + "/";
  }

  async getAllDepartments(): Promise<string[]> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const allDepartmentsResponse = await fetch(
        this.REQUEST_URI + this.DEPARTMENT,
        requestOptions as RequestInit
    );
    if (allDepartmentsResponse.status === this.RESPONSE_STATUS_OK) {
      const allDepartmentsJSON = await allDepartmentsResponse.json();
      const allDepartments = allDepartmentsJSON;
      return allDepartments;
    }
    return [];
  }

  async getAllUserAccounts(): Promise<IAccountDetails[]> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const allUserAccountsResponse = await fetch(
        this.REQUEST_URI + this.USERS + this.ASSESSMENTS_NUMBER,
        requestOptions as RequestInit
    );
    if (allUserAccountsResponse.body) {
      const userAccountsResponseBody = await allUserAccountsResponse.json();
      return userAccountsResponseBody;
    }
    return [];
  }

  async createAccount(accountInformation: IAccountDetails) {
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.POST,
        accountInformation
    );
    const createAccountResponse = await fetch(
        this.REQUEST_URI + this.USERS,
        requestOptions as RequestInit
    );
    if (createAccountResponse.status === this.RESPONSE_STATUS_OK) {
      return accountInformation;
    } else {
      return false;
    }
  }

  async updateOwnAccount(accountInformation: IAccountDetails) {
    delete accountInformation.privileges;
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.PUT,
        accountInformation
    );
    const updateAccountResponse = await fetch(
        this.REQUEST_URI + this.USERS + "/" + this.UPDATE_SELF,
        requestOptions as RequestInit
    );
    if (updateAccountResponse.status === this.RESPONSE_STATUS_OK) {
      return accountInformation;
    } else {
      return false;
    }
  }

  async updateUserAccount(accountInformation: IAccountDetails, userId: string) {
    delete accountInformation.privileges;
    delete (accountInformation as any).numberOfAssessments;
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.PUT,
        accountInformation
    );
    const updateAccountResponse = await fetch(
        this.REQUEST_URI + this.USERS + this.EDIT + userId,
        requestOptions as RequestInit
    );
    if (updateAccountResponse.status === this.RESPONSE_STATUS_OK) {
      return accountInformation;
    } else {
      return false;
    }
  }

  async getSpecificUserAssessments(username: string): Promise<IAssessment[]> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const userAssessmentResponse = await fetch(
        this.REQUEST_URI +
        this.ADMIN +
        this.SEARCH +
        this.ASSESSMENTS +
        this.USERS +
        "/" +
        username,
        requestOptions as RequestInit
    );
    const userAssessmentsJSON = await userAssessmentResponse.json();
    const isValidResponse =
        userAssessmentsJSON && Array.isArray(userAssessmentsJSON);
    if (isValidResponse) {
      const userAssessmentList = userAssessmentsJSON;
      const fetchedAssessments: IAssessment[] = await userAssessmentList.map(
          (assessmentResponseObject: IAssessment) => {
            const assessmentEntry: IAssessment = assessmentResponseObject;
            assessmentEntry.outputs = assembleAssessmentOutputs(
                assessmentResponseObject.inputs
            );
            return assessmentEntry;
          }
      );
      return fetchedAssessments;
    } else {
      return [] as IAssessment[];
    }
  }
  async isSessionActive() {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const sessionStatusResponse = await fetch(
        this.REQUEST_URI + this.ASSESSMENTS,
        requestOptions as RequestInit
    );
    if (sessionStatusResponse.status === this.RESPONSE_STATUS_OK) {
      return true;
    } else {
      return false;
    }
  }

  async getOwnOrganization(): Promise<IOrganization | null> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const ownOrgResponse = await fetch(
        this.REQUEST_URI + "organization/self",
        requestOptions as RequestInit
    );

    if (ownOrgResponse.status === this.RESPONSE_STATUS_OK) {
      return await ownOrgResponse.json();
    }

    return null;
  }

  async updateOwnOrganization(
      organization: IOrganization
  ): Promise<IOrganization | null> {
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.PUT,
        organization
    );
    const ownOrgResponse = await fetch(
        this.REQUEST_URI + "organization/self",
        requestOptions as RequestInit
    );

    if (ownOrgResponse.status === this.RESPONSE_STATUS_OK) {
      return await ownOrgResponse.json();
    }

    return null;
  }

  async getOrchestratorReleases(token?: string): Promise<IReleaseResponse | null> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const releaseResponse = await fetch(
        this.REQUEST_URI + "orchestrator/releases" + (token ? `/t/${token}` : ""),
        requestOptions as RequestInit
    );

    if (releaseResponse.status === this.RESPONSE_STATUS_OK) {
      return await releaseResponse.json();
    }

    return null;
  }

  async getOrchestratorRelease(releaseId: number, token?: string): Promise<IRelease | null> {
    const releasesResponse: IReleaseResponse | null = await this.getOrchestratorReleases(token);
    if (releasesResponse === null || releasesResponse.value === null) {
      return null;
    }

    const matchedRelease: IRelease | undefined = releasesResponse.value.find(
        (rel: IRelease) => rel.Id === releaseId
    );
    if (matchedRelease) {
      if (typeof matchedRelease.Arguments.Input === "string") {
        matchedRelease.Arguments.Input = JSON.parse(
            matchedRelease.Arguments.Input
        );
      }
      return matchedRelease;
    }
    return null;
  }

  async startNewJob(
    releaseKey: string,
    inputArguments: string = "",
    organisationalUnitId: string,
    token?: string
  ): Promise<any | null> {
    // inputArguments = inputArguments.replace(/"/g, '\\"');
    const requestOptions = createRequestOptions(
      BackendRestAPIRequestTypes.POST,
      { releaseKey, inputArguments, organisationalUnitId }
    );
    const response = await fetch(
      this.REQUEST_URI + "orchestrator/startNewJob" + (token ? `/t/${token}` : ""),
      requestOptions as RequestInit
    );

    if (response.status === this.RESPONSE_STATUS_OK) {
      return await response.json();
    }

    return null;
  }

  // TODO here
  async getUiPathProcessFormByReleaseId(
      releaseId: string
  ): Promise<any | null> {
    const requestOptions = createRequestOptions(BackendRestAPIRequestTypes.GET);
    const formResponse = await fetch(
        this.REQUEST_URI + "orchestrator/releases/" + releaseId + "/form",
        requestOptions as RequestInit
    );

    if (formResponse.status === this.RESPONSE_STATUS_OK) {
      return await formResponse.json();
    }

    return null;
  }

  async saveUiPathProcessForm(processForm: IProcessForm): Promise<IProcessForm | null> {
    const requestOptions = createRequestOptions(
        processForm.id
            ? BackendRestAPIRequestTypes.PUT
            : BackendRestAPIRequestTypes.POST,
        processForm
    );
    const response = await fetch(
        this.REQUEST_URI + this.PROCESS_FORMS + "/" + (processForm.id || ""),
        requestOptions as RequestInit
    );

    if (response.status === this.RESPONSE_STATUS_OK) {
      return response.json();
    } else {
      return null;
    }
  }

  async generateProcessFromLink(processId: string): Promise<string | null> {
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.GET,
        null
    );
    const response = await fetch(
        this.REQUEST_URI + this.GENERATE_PROCESS_TOKEN + "/" + processId,
        requestOptions as RequestInit
    );

    if (response.status === this.RESPONSE_STATUS_OK) {
      return document.location.origin + '/form/' + await response.text();
    } else {
      return null;
    }
  }

  async redeemOrchestratorProcessToken(token: string): Promise<IProcessForm | null> {
    const requestOptions = createRequestOptions(
        BackendRestAPIRequestTypes.GET,
        null
    );
    const response = await fetch(
        this.REQUEST_URI + this.REDEEM_PROCESS_TOKEN + "/" + token,
        requestOptions as RequestInit
    );

    if (response.status === this.RESPONSE_STATUS_OK) {
      return response.json();
    } else {
      return null;
    }
  }
}

export default new BackendApi();
